/* Napurel homepage behaviour: count-up numbers, color showcase, mattress-depth fit diagram. */
(function () {
  if (window.__ppHomeLoaded) return;
  window.__ppHomeLoaded = true;
  const reduce = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Count-up numbers (once, when the band is in view) ---------- */
  function initCounts() {
    const groups = document.querySelectorAll('[data-pp-count-group]');
    if (!groups.length) return;
    const run = (group) => {
      group.querySelectorAll('[data-pp-count]').forEach((el) => {
        const target = Number(el.dataset.ppCount) || 0;
        if (reduce() || target === 0) {
          el.textContent = target;
          return;
        }
        const start = performance.now();
        const duration = 1100;
        const tick = (now) => {
          const p = Math.min(1, (now - start) / duration);
          el.textContent = Math.round(target * (1 - Math.pow(1 - p, 3)));
          if (p < 1) requestAnimationFrame(tick);
        };
        el.textContent = 0;
        requestAnimationFrame(tick);
      });
    };
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            run(entry.target);
            io.unobserve(entry.target);
          }
        }),
      { threshold: 0.4 }
    );
    groups.forEach((g) => io.observe(g));
  }

  /* ---------- Color showcase ---------- */
  if (!customElements.get('pp-color-show')) {
    class PpColorShow extends HTMLElement {
      connectedCallback() {
        this.chips = Array.from(this.querySelectorAll('[data-pp-color]'));
        this.panels = Array.from(this.querySelectorAll('[data-pp-color-panel]'));
        this.chips.forEach((chip) => {
          chip.addEventListener('click', () => this.select(Number(chip.dataset.ppColor)));
          chip.addEventListener('keydown', (event) => {
            const i = this.chips.indexOf(chip);
            const step = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
            if (!step) return;
            event.preventDefault();
            const next = (i + step + this.chips.length) % this.chips.length;
            this.chips[next].focus();
            this.select(next);
          });
        });
      }

      select(index) {
        this.chips.forEach((c, i) => {
          c.classList.toggle('is-active', i === index);
          c.setAttribute('aria-checked', String(i === index));
          c.tabIndex = i === index ? 0 : -1;
        });
        this.panels.forEach((p, i) => p.classList.toggle('is-active', i === index));
      }
    }
    customElements.define('pp-color-show', PpColorShow);
  }

  /* ---------- Depth fit: slider drives verdict + side-view diagram ---------- */
  if (!customElements.get('pp-depth-fit')) {
    class PpDepthFit extends HTMLElement {
      connectedCallback() {
        this.range = this.querySelector('[data-pp-depth]');
        this.out = this.querySelector('[data-pp-depth-out]');
        this.result = this.querySelector('.pp-hfit__result');
        this.head = this.querySelector('[data-pp-fit-head]');
        this.text = this.querySelector('[data-pp-fit-text]');
        this.mattress = this.querySelector('[data-pp-mattress]');
        this.comforter = this.querySelector('[data-pp-comforter]');
        this.pocket = this.querySelector('[data-pp-pocket]');
        this.pocketLabel = this.querySelector('[data-pp-pocket-label]');
        this.drapeLabel = this.querySelector('[data-pp-drape-label]');
        this.labels = (this.dataset.labels || '').split('|');
        this.texts = (this.dataset.texts || '').split('|');
        this.drape = Number(this.dataset.drape) || 14;
        this.pocketIn = Number(this.dataset.pocket) || 14;
        this.range.addEventListener('input', () => this.update());
        this.update();
      }

      update() {
        const d = Number(this.range.value);
        const min = Number(this.range.min);
        const max = Number(this.range.max);
        this.range.style.setProperty('--pp-fill', `${((d - min) / (max - min)) * 100}%`);
        this.out.textContent = `${d} in`;

        // Verdict buckets follow the measured Queen results: 8-10 / 11-12 / 13-14 / 15+
        const bucket = d <= 10 ? 0 : d <= 12 ? 1 : d <= 14 ? 2 : 3;
        const state = bucket <= 1 ? 'good' : bucket === 2 ? 'snug' : 'no';
        this.result.dataset.state = state;
        this.head.textContent = this.labels[bucket <= 1 ? 0 : bucket === 2 ? 1 : 2] || '';
        this.text.textContent = this.texts[bucket] || '';

        // Side view: 1 in = 12 px. Floor at y=268, frame on legs.
        const k = 12;
        const floor = 268;
        const frameTop = floor - 40;
        const top = frameTop - d * k;
        const x0 = 80;
        const x1 = 320;
        this.mattress.setAttribute('y', top);
        this.mattress.setAttribute('height', d * k);
        const hang = Math.min(this.drape * k, frameTop - top + 30);
        const cy = top - 3;
        this.comforter.setAttribute(
          'd',
          `M${x0 - 4} ${cy + hang} L${x0 - 4} ${cy + 6} Q${x0 - 4} ${cy} ${x0 + 6} ${cy} L${x1 - 6} ${cy} Q${x1 + 4} ${cy} ${x1 + 4} ${cy + 6} L${x1 + 4} ${cy + hang}`
        );
        this.pocket.setAttribute('y1', top);
        this.pocket.setAttribute('y2', top + this.pocketIn * k);
        this.pocket.style.stroke = this.pocketIn < d ? 'var(--pp-fit-no)' : '';
        this.pocketLabel.setAttribute('y', Math.max(14, top + (this.pocketIn * k) / 2));
        this.drapeLabel.setAttribute('y', Math.max(14, cy + hang + 16));
        this.drapeLabel.setAttribute('x', 8);
      }
    }
    customElements.define('pp-depth-fit', PpDepthFit);
  }

  /* ---------- Mobile sticky shop bar: after the hero, hidden over the product card and footer ---------- */
  if (!customElements.get('pp-home-bar')) {
    class PpHomeBar extends HTMLElement {
      connectedCallback() {
        const hero = this.closest('.shopify-section')?.querySelector('.pp-hero__frame');
        const blockers = Array.from(document.querySelectorAll('.pp-hprod, .footer, .pp-footer'));
        const link = this.querySelector('a');
        let pastHero = false;
        const inView = new Set();
        const update = () => {
          const show = pastHero && inView.size === 0;
          this.classList.toggle('is-visible', show);
          this.setAttribute('aria-hidden', String(!show));
          if (link) link.tabIndex = show ? 0 : -1;
        };
        if (!('IntersectionObserver' in window) || !hero) return;
        new IntersectionObserver(([entry]) => {
          pastHero = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          update();
        }).observe(hero);
        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => (entry.isIntersecting ? inView.add(entry.target) : inView.delete(entry.target)));
          update();
        });
        blockers.forEach((el) => io.observe(el));
      }
    }
    customElements.define('pp-home-bar', PpHomeBar);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initCounts);
  else initCounts();
})();
